import { useEffect } from "react";
import { Box, Container, Paper, Typography } from "@mui/material";
import { useDocumentosLegais } from "../Legal/useDocumentosLegais";
import { DocumentosPorTipo } from "../Legal/buscarDocumentosLegais";
import { SECOES_LEGAIS, SecaoLegal, TIPOS_LEGAIS } from "../Legal/secoes";
import { DocumentoLegal } from "../Legal/tipos";
import IndiceLegal from "./IndiceLegal";
import SecaoDocumento from "./SecaoDocumento";
import MensagemCentralizada from "./MensagemCentralizada";

export interface PaginaLegalProps {
  /** Âncora para onde rolar ao abrir, quando a URL aponta para um documento. */
  ancoraInicial: string | null;
}

interface DocumentoDaSecao {
  secao: SecaoLegal;
  documento: DocumentoLegal;
}

/** Descarta seções cujo documento a API ainda não tem, preservando a ordem. */
const emparelharComDocumentos = (
  documentos: DocumentosPorTipo,
): DocumentoDaSecao[] =>
  SECOES_LEGAIS.flatMap((secao) => {
    const documento = documentos[secao.tipo];
    return documento ? [{ secao, documento }] : [];
  });

/** Página aberta (sem login) com todos os documentos legais em sequência. */
const PaginaLegal = ({ ancoraInicial }: PaginaLegalProps) => {
  const { documentos, carregando, erro } = useDocumentosLegais(TIPOS_LEGAIS);

  useEffect(() => {
    document.title = "Documentos legais | Sauhde";
  }, []);

  useEffect(() => {
    if (carregando || !ancoraInicial) return;
    document.getElementById(ancoraInicial)?.scrollIntoView();
  }, [carregando, ancoraInicial]);

  if (carregando) return <MensagemCentralizada texto="Carregando…" />;
  if (erro) return <MensagemCentralizada texto={erro} ehErro />;

  const itens = emparelharComDocumentos(documentos);
  if (itens.length === 0) {
    return (
      <MensagemCentralizada
        texto="Nenhum documento legal foi publicado ainda."
        ehErro
      />
    );
  }

  return (
    <Box sx={{ backgroundColor: "background.default", minHeight: "100vh" }}>
      <Container maxWidth="md" sx={{ paddingY: { xs: 3, md: 6 } }}>
        <Paper elevation={0} sx={{ padding: { xs: 2.5, md: 5 } }}>
          <Box
            component="img"
            src="/LOGO-COMPLETA.svg"
            alt="Sauhde"
            sx={{ height: 40, marginBottom: 3 }}
          />
          <Typography variant="h2" component="h1" gutterBottom>
            Documentos legais
          </Typography>
          <IndiceLegal secoes={itens.map((item) => item.secao)} />
          {itens.map(({ secao, documento }) => (
            <SecaoDocumento
              key={secao.ancora}
              secao={secao}
              documento={documento}
            />
          ))}
        </Paper>
      </Container>
    </Box>
  );
};

export default PaginaLegal;
